<script lang="ts">
  // components
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  // libs
  import { form as formHandler, bindClass } from "svelte-forms";

  // types
  import type { DistributorCredentialData } from "@shared/types/DistributorCredential";

  // stores
  import { CredentialManagementStore } from "@retailer/stores/CredentialManagementStore";

  // props
  export let showModal: boolean;
  export let closeModal: () => void;
  export let isSubmitting: boolean = false;
  export let addDistributorCredentials: (data: DistributorCredentialData) => void;

  // Form data
  let selectedDistributor: number = null;
  let hostname = "";
  let port: number = null;
  let directory = "";
  let sftpId = "";
  let sftpPassword = "";
  let sftpSshKey = "";
  let as2Name = "";
  let as2Email = "";
  let as2Certificate = "";

  $: selectedTransferMethod = "";
  $: selectedAuthMethod = "";

  // functions
  const credentialForm = formHandler(() => ({
    distributor_id: { value: selectedDistributor, validators: ["required"] },
    file_transfer_type: { value: selectedTransferMethod, validators: ["required"] },
    server_hostname: { value: hostname, validators: ["required"] },
    server_port: { value: port, validators: ["required"] },
    server_directory: { value: directory },
    id: { value: sftpId, validators: selectedTransferMethod === "sftp" ? ["required"] : [] },
    password: {
      value: sftpPassword,
      validators:
        selectedTransferMethod === "sftp" &&
        (selectedAuthMethod === "ID + Password" || selectedAuthMethod === "ID + Password + SSH Key")
          ? ["required"]
          : [],
    },
    ssh_key: {
      value: sftpSshKey,
      validators:
        selectedTransferMethod === "sftp" &&
        (selectedAuthMethod === "ID + SSH Key" || selectedAuthMethod === "ID + Password + SSH Key")
          ? ["required"]
          : [],
    },
    as2_name: { value: as2Name, validators: selectedTransferMethod === "as2" ? ["required"] : [] },
    email: { value: as2Email, validators: selectedTransferMethod === "as2" ? ["required"] : [] },
    certificate: {
      value: as2Certificate,
      validators: selectedTransferMethod === "as2" ? ["required"] : [],
    },
  }));

  const resetOnDistributorSelect = () => {
    selectedTransferMethod = "";
    resetOnTransferMethodSelect();
  };

  const resetOnTransferMethodSelect = () => {
    hostname = "";
    port = null;
    directory = "";
    selectedAuthMethod = "";
    as2Name = "";
    as2Email = "";
    as2Certificate = "";
    resetOnAuthTypeSelect();
  };

  const resetOnAuthTypeSelect = () => {
    sftpId = "";
    sftpPassword = "";
    sftpSshKey = "";
  };

  const resetForm = () => {
    selectedDistributor = null;
    resetOnDistributorSelect();
  };

  const onClose = () => {
    closeModal();
    resetForm();
  };

  const onSubmit = () => {
    addDistributorCredentials({
      distributor_id: selectedDistributor,
      file_transfer_type: selectedTransferMethod,
      server_hostname: hostname,
      server_port: port,
      server_directory: directory,
      authentication_type: selectedAuthMethod,
      authentication: {
        id: sftpId,
        password: sftpPassword,
        ssh_key: sftpSshKey,
      },
      as2_name: as2Name,
      email: as2Email,
      certificate: as2Certificate,
    });
    resetForm();
  };
</script>

<Modal
  bind:show={showModal}
  closeOnBackgroundClick={false}
  fizzModal={true}
  size={"medium"}
  overflowY="scroll"
>
  <section class="fizz-inset-square-24" id="credential-management-modal">
    <header class="fizz-space-between fizz-stack-24">
      <h4 class="fizz-heading-4 fizz-text-large">Add Distributor</h4>
      <div data-testId="credential-management-modal-close-button" on:click|preventDefault={onClose}>
        <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="16" width="16" />
      </div>
    </header>
    <div class="fizz-stack-24">
      <p>Choose a Distributor and protocol to enter in their credentials</p>
    </div>
    <form id="credentialform">
      <div class="fizz-stack-24">
        <div class="fizz-stack-16">
          <label class="fizz-label fizz-stack-4" for="select">Distributor</label>
          <select
            class="fizz-select fizz-full-width"
            name="distributorSelect"
            id="distributor-select"
            data-testId="distributor-select"
            bind:value={selectedDistributor}
            use:bindClass={{ form: credentialForm }}
            on:change={resetOnDistributorSelect}
          >
            <option value={null}>Select a Distributor</option>
            {#each $CredentialManagementStore.distributors as d}
              <option value={d.id}>{d.name}</option>
            {/each}
          </select>
        </div>

        <div class="fizz-stack-16">
          <label class="fizz-label fizz-stack-4" for="select">File transfer method</label>
          <select
            class="fizz-select fizz-full-width"
            name="fileTransferMethodSelect"
            id="file-transfer-method-select"
            data-testId="file-transfer-method-select"
            bind:value={selectedTransferMethod}
            use:bindClass={{ form: credentialForm }}
            on:change={resetOnTransferMethodSelect}
          >
            <option value="">Select a method</option>
            <option value={"sftp"}>SSH (sftp/scp)</option>
            <option value={"as2"} hidden>AS2</option>
          </select>
        </div>

        {#if selectedTransferMethod === "as2"}
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="input-text">As2Name</label>
            <div class="fizz-flex-row">
              <input
                type="text"
                id="as2-name"
                name="as2-name"
                placeholder="Enter a name"
                class="fizz-input-text fizz-flex-item"
                data-testid="as2-name"
                bind:value={as2Name}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
          </div>
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="input-text">Email</label>
            <div class="fizz-flex-row">
              <input
                type="text"
                id="as2-email"
                name="as2-email"
                placeholder="Enter an email"
                class="fizz-input-text fizz-flex-item"
                data-testid="as2-email"
                bind:value={as2Email}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
          </div>
        {/if}

        {#if selectedTransferMethod !== ""}
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="input-text">Hostname</label>
            <div class="fizz-flex-row">
              <input
                type="text"
                id="hostname"
                name="hostname"
                placeholder="Enter a hostname"
                class="fizz-input-text fizz-flex-item"
                data-testid="hostname"
                bind:value={hostname}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
          </div>
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="input-text">Port</label>
            <div class="fizz-flex-row">
              <input
                type="number"
                id="port"
                name="port"
                placeholder="Enter a port"
                class="fizz-input-text fizz-flex-item"
                data-testid="port"
                bind:value={port}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
          </div>
          <div class="fizz-stack-16">
            <div class="fizz-flex-row fizz-gap-4 fizz-stack-4">
              <label class="fizz-label fizz-inline" for="input-text">Directory </label>
              <p>(optional)</p>
            </div>

            <div class="fizz-flex-row">
              <input
                type="text"
                id="directory"
                name="directory"
                placeholder="Enter a directory"
                class="fizz-input-text fizz-flex-item"
                data-testid="directory"
                bind:value={directory}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
          </div>
        {/if}
        {#if selectedTransferMethod === "sftp"}
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="select">Authentication</label>
            <select
              class="fizz-select fizz-full-width"
              name="sftp-authentication"
              id="sftp-auth-select"
              data-testid="sftp-auth-select"
              bind:value={selectedAuthMethod}
              use:bindClass={{ form: credentialForm }}
              on:change={resetOnAuthTypeSelect}
            >
              <option value="">Select an authentication type</option>
              <option value={"ID + Password"}>ID + Password</option>
              <option value={"ID + Password + SSH Key"}>ID + Password + SSH Key</option>
              <option value={"ID + SSH Key"}>ID + SSH Key</option>
            </select>
          </div>
          {#if selectedAuthMethod !== ""}
            <div class="fizz-stack-16">
              <div class="fizz-flex-row fizz-gap-4 fizz-stack-4">
                <label class="fizz-label fizz-inline" for="input-text">ID</label>
              </div>
              <div class="fizz-flex-row">
                <input
                  type="text"
                  id="sftp-id"
                  name="sftp-id"
                  placeholder="Enter an id"
                  class="fizz-input-text fizz-flex-item fizz-inline"
                  data-testid="id-auth-field"
                  bind:value={sftpId}
                  use:bindClass={{ form: credentialForm }}
                />
              </div>
            </div>
          {/if}
          {#if selectedAuthMethod === "ID + Password" || selectedAuthMethod === "ID + Password + SSH Key"}
            <div class="fizz-stack-16">
              <div class="fizz-flex-row fizz-gap-4 fizz-stack-4">
                <label class="fizz-label fizz-inline" for="input-text">Password</label>
              </div>
              <div class="fizz-flex-row">
                <input
                  type="text"
                  id="sftp-password"
                  name="sftp-password"
                  placeholder="Enter a password"
                  class="fizz-input-text fizz-flex-item"
                  data-testid="pass-auth-field"
                  bind:value={sftpPassword}
                  use:bindClass={{ form: credentialForm }}
                />
              </div>
            </div>
          {/if}
          {#if selectedAuthMethod === "ID + SSH Key" || selectedAuthMethod === "ID + Password + SSH Key"}
            <div class="fizz-stack-16">
              <div class="fizz-flex-row fizz-gap-4 fizz-stack-4">
                <label class="fizz-label fizz-inline" for="input-text">SSH Key</label>
              </div>
              <div class="fizz-flex-row">
                <input
                  type="text"
                  id="sftp-sshKey"
                  name="sftp-sshKey"
                  placeholder="Enter a key"
                  class="fizz-input-text fizz-flex-item"
                  data-testid="ssh-auth-field"
                  bind:value={sftpSshKey}
                  use:bindClass={{ form: credentialForm }}
                />
              </div>
            </div>
          {/if}
        {/if}
        {#if selectedTransferMethod === "as2"}
          <div class="fizz-stack-16">
            <label class="fizz-label fizz-stack-4" for="input-text">SSL Certificate</label>
            <div class="fizz-flex-row">
              <input
                type="text"
                id="as2-certificate"
                name="as2-certificate"
                placeholder="Enter a certificate"
                class="fizz-input-text fizz-flex-item"
                data-testid="as2-certificate"
                bind:value={as2Certificate}
                use:bindClass={{ form: credentialForm }}
              />
            </div>
            <p class="fizz-text-subdued">Exchanged at setup</p>
          </div>
        {/if}
      </div>

      <footer class="fizz-flex-row fizz-justify-end">
        <button class="fizz-button" on:click|preventDefault={onClose}>Cancel</button>
        <button
          class="fizz-button-secondary"
          disabled={isSubmitting || !$credentialForm.valid}
          on:click|preventDefault={onSubmit}
        >
          Add
        </button>
      </footer>
    </form>
  </section>
</Modal>
